import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdCard,
  faShieldAlt,
  faCheckCircle,
  faCircleExclamation,
  faEye,
  faEyeSlash,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import PhotoChange from '../components/ProfilePhoto/PhotoChange';
import './Settings.css';

const API = process.env.REACT_APP_API_BASE_URL;
const MIN_PASSWORD = 6;
const GRADES = Array.from({ length: 12 }, (_, i) => i + 1);

const Message = ({ msg }) =>
  msg?.text ? (
    <p className={`rmset-msg is-${msg.type}`} role={msg.type === 'error' ? 'alert' : 'status'}>
      <FontAwesomeIcon icon={msg.type === 'success' ? faCheckCircle : faCircleExclamation} /> {msg.text}
    </p>
  ) : null;

const StudentSettings = ({ userData }) => {
  const studentId = userData?.id;
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null); // read-only fields + image
  const [form, setForm] = useState({ dateOfBirth: '', grade: '' });
  const [saved, setSaved] = useState({ dateOfBirth: '', grade: '' });
  const [profileMsg, setProfileMsg] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [savingPw, setSavingPw] = useState(false);

  // ---------- Data (same endpoint) ----------
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/api/student/studentsettings/${studentId}`);
        const s = response.data.student || {};
        if (cancelled) return;
        const editable = {
          dateOfBirth: s.birthday ? String(s.birthday).split('T')[0] : '',
          grade: s.Grade ? String(s.Grade) : '',
        };
        setProfile({
          firstName: s.firstName || '',
          lastName: s.lastName || '',
          mobileNumber: s.mobileNumber || '',
          parentsEmail: s.parentsEmail || '',
          image: s.image || '',
        });
        setForm(editable);
        setSaved(editable);
      } catch (error) {
        if (!cancelled) setProfileMsg({ type: 'error', text: 'We couldn’t load your details. Please refresh the page.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  const profileChanged = form.dateOfBirth !== saved.dateOfBirth || form.grade !== saved.grade;

  // ---------- Save birthday + grade (same endpoint) ----------
  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profileChanged) return;
    setSavingProfile(true);
    setProfileMsg(null);
    try {
      await axios.put(`${API}/api/student/studentsettings`, {
        studentId,
        dateOfBirth: form.dateOfBirth,
        grade: form.grade,
      });
      setSaved(form);
      setProfileMsg({ type: 'success', text: 'Your details were saved.' });
    } catch (error) {
      setProfileMsg({ type: 'error', text: error.response?.data?.message || 'We couldn’t save your details. Please try again.' });
    } finally {
      setSavingProfile(false);
    }
  };

  // ---------- Change password (same endpoint) ----------
  const savePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (!pw.currentPassword || !pw.newPassword || !pw.confirmPassword) {
      setPwMsg({ type: 'error', text: 'Please fill in all three fields.' });
      return;
    }
    if (pw.newPassword.length < MIN_PASSWORD) {
      setPwMsg({ type: 'error', text: `The new password needs at least ${MIN_PASSWORD} characters.` });
      return;
    }
    if (pw.newPassword !== pw.confirmPassword) {
      setPwMsg({ type: 'error', text: 'The new passwords don’t match.' });
      return;
    }
    if (pw.newPassword === pw.currentPassword) {
      setPwMsg({ type: 'error', text: 'The new password must be different from the current one.' });
      return;
    }
    setSavingPw(true);
    try {
      await axios.post(`${API}/api/student/studentsettings/passwordchange`, {
        studentId,
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      });
      setPw({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPwMsg({ type: 'success', text: 'Your password was changed.' });
    } catch (error) {
      setPwMsg({ type: 'error', text: error.response?.data?.message || 'We couldn’t change the password. Check your current password.' });
    } finally {
      setSavingPw(false);
    }
  };

  const onPw = (e) => {
    setPw((p) => ({ ...p, [e.target.name]: e.target.value }));
    setPwMsg(null);
  };

  const fullName = profile ? [profile.firstName, profile.lastName].filter(Boolean).join(' ') : userData?.fullName || '';
  const initials =
    (fullName || 'S')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join('') || 'S';

  const changeUrl = `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(
    `Hi! I'm ${fullName || 'a RoboMind student'} and I'd like to update my account details.`
  )}&type=phone_number&app_absent=0`;

  const today = new Date().toISOString().split('T')[0];

  if (loading || !studentId) {
    return (
      <div className="rmset" aria-busy="true">
        <div className="rmset-inner">
          <div className="rmset-skel rmset-skel--title" />
          <div className="rmset-skel rmset-skel--card" />
          <div className="rmset-skel rmset-skel--card" />
        </div>
      </div>
    );
  }

  return (
    <div className="rmset">
      <div className="rmset-inner">
        <header className="rmset-head">
          <span className="rmset-avatar" aria-hidden="true">
            {profile?.image ? <img src={profile.image} alt="" /> : initials}
          </span>
          <div>
            <span className="rmset-eyebrow">Settings</span>
            <h1 className="rmset-title">{fullName || 'Your account'}</h1>
            <p className="rmset-sub">Your details and password.</p>
          </div>
        </header>

        <PhotoChange role="student" />

        {/* ---------- Profile ---------- */}
        <form className="rmset-card" onSubmit={saveProfile} noValidate aria-labelledby="rmset-profile-title">
          <h2 id="rmset-profile-title" className="rmset-card-title">
            <FontAwesomeIcon icon={faIdCard} /> Personal details
          </h2>

          {profile && (
            <dl className="rmset-info">
              <div>
                <dt>First name</dt>
                <dd>{profile.firstName || '—'}</dd>
              </div>
              <div>
                <dt>Last name</dt>
                <dd>{profile.lastName || '—'}</dd>
              </div>
              <div>
                <dt>Mobile number</dt>
                <dd>{profile.mobileNumber || '—'}</dd>
              </div>
              <div>
                <dt>Parent’s email</dt>
                <dd>{profile.parentsEmail || '—'}</dd>
              </div>
            </dl>
          )}
          <p className="rmset-hint">
            <FontAwesomeIcon icon={faLock} /> To change these,{' '}
            <a href={changeUrl} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faWhatsapp} /> message us
            </a>
            .
          </p>

          <div className="rmset-row">
            <div className="rmset-field">
              <label htmlFor="rmset-dob">Date of birth</label>
              <input
                id="rmset-dob"
                type="date"
                className="rmset-input"
                value={form.dateOfBirth}
                max={today}
                onChange={(e) => {
                  setForm((f) => ({ ...f, dateOfBirth: e.target.value }));
                  setProfileMsg(null);
                }}
              />
            </div>
            <div className="rmset-field">
              <label htmlFor="rmset-grade">School grade</label>
              <select
                id="rmset-grade"
                className="rmset-input rmset-select"
                value={form.grade}
                onChange={(e) => {
                  setForm((f) => ({ ...f, grade: e.target.value }));
                  setProfileMsg(null);
                }}
              >
                <option value="">Select grade</option>
                {GRADES.map((g) => (
                  <option key={g} value={String(g)}>
                    Grade {g}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Message msg={profileMsg} />

          <div className="rmset-actions">
            {profileChanged && (
              <button
                type="button"
                className="rmset-btn rmset-btn--ghost"
                onClick={() => {
                  setForm(saved);
                  setProfileMsg(null);
                }}
                disabled={savingProfile}
              >
                Cancel
              </button>
            )}
            <button type="submit" className="rmset-btn" disabled={!profileChanged || savingProfile}>
              {savingProfile ? 'Saving…' : 'Save details'}
            </button>
          </div>
        </form>

        {/* ---------- Password ---------- */}
        <form className="rmset-card" onSubmit={savePassword} noValidate aria-labelledby="rmset-pw-title">
          <h2 id="rmset-pw-title" className="rmset-card-title">
            <FontAwesomeIcon icon={faShieldAlt} /> Change password
          </h2>

          <div className="rmset-row rmset-row--3">
            <div className="rmset-field">
              <label htmlFor="rmset-pw-current">Current password</label>
              <input
                id="rmset-pw-current"
                name="currentPassword"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                className="rmset-input"
                value={pw.currentPassword}
                onChange={onPw}
              />
            </div>
            <div className="rmset-field">
              <label htmlFor="rmset-pw-new">New password</label>
              <input
                id="rmset-pw-new"
                name="newPassword"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                className="rmset-input"
                value={pw.newPassword}
                onChange={onPw}
                aria-describedby="rmset-pw-rule"
              />
              <small id="rmset-pw-rule" className="rmset-rule">
                At least {MIN_PASSWORD} characters
              </small>
            </div>
            <div className="rmset-field">
              <label htmlFor="rmset-pw-confirm">Confirm new password</label>
              <input
                id="rmset-pw-confirm"
                name="confirmPassword"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                className="rmset-input"
                value={pw.confirmPassword}
                onChange={onPw}
              />
            </div>
          </div>

          <button type="button" className="rmset-show" onClick={() => setShowPw((v) => !v)} aria-pressed={showPw}>
            <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye} /> {showPw ? 'Hide passwords' : 'Show passwords'}
          </button>

          <Message msg={pwMsg} />

          <div className="rmset-actions">
            <button type="submit" className="rmset-btn" disabled={savingPw}>
              {savingPw ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentSettings;